import type { PendingChatMessage } from "../types/message";

export type GroupedMessage = PendingChatMessage & {
  isFirstInGroup: boolean;
  isLastInGroup: boolean;
  showAvatar: boolean;
  showName: boolean;
  showTime: boolean;
};

export interface MessageGroup {
  date: string;
  messages: GroupedMessage[];
}

/**
 * Groups messages by date and sender for UI rendering.
 *
 * Rules:
 * 1. Messages are first grouped by date (YYYY-MM-DD).
 * 2. Consecutive messages from the same sender within a date are grouped.
 * 3. `isFirstInGroup`: First message of a sender sequence.
 * 4. `isLastInGroup`: Last message of a sender sequence.
 * 5. `showAvatar`: Only for the first message of 'other' sender group.
 * 6. `showName`: Only for the first message of 'other' sender group.
 * 7. `showTime`: Only for the last message of any group.
 */
export function groupMessages(
  messages: PendingChatMessage[],
  currentUserId: string,
): MessageGroup[] {
  if (!messages || messages.length === 0) return [];

  const groups: MessageGroup[] = [];
  let currentGroup: MessageGroup | null = null;

  messages.forEach((message, index) => {
    const messageDate = new Date(message.createdAt).toDateString();

    // Start a new date group if needed
    if (currentGroup?.date !== messageDate) {
      currentGroup = {
        date: messageDate,
        messages: [],
      };
      groups.push(currentGroup);
    }

    const prevMessage = index > 0 ? messages[index - 1] : null;
    const nextMessage =
      index < messages.length - 1 ? messages[index + 1] : null;

    // Check strict equality for sender ID
    // System messages break the chain
    const isSameSenderAsPrev = prevMessage
      ? prevMessage.senderId === message.senderId &&
        prevMessage.messageType !== "system" &&
        message.messageType !== "system"
      : false;

    const isSameSenderAsNext = nextMessage
      ? nextMessage.senderId === message.senderId &&
        nextMessage.messageType !== "system" &&
        message.messageType !== "system"
      : false;

    // Determine grouping flags
    const isFirstInGroup = !isSameSenderAsPrev;
    const isLastInGroup = !isSameSenderAsNext;

    // Determine UI visibility flags
    // Avatar/Name: Show on first message of group, only for others
    const isOther = message.senderId !== currentUserId;
    const showAvatar = isOther && isFirstInGroup;
    const showName = isOther && isFirstInGroup;

    // Time: Show time if the minute changes or the sender changes
    const currentMinute = new Date(message.createdAt).setSeconds(0, 0);
    const nextMinute = nextMessage
      ? new Date(nextMessage.createdAt).setSeconds(0, 0)
      : null;
    const isSameMinuteAsNext =
      nextMinute !== null && currentMinute === nextMinute;

    // We only hide the time if it's the SAME SENDER and the SAME MINUTE as the NEXT message.
    // That means only the LAST message of that minute/sender group shows the time.
    const showTime = !isSameSenderAsNext || !isSameMinuteAsNext;

    currentGroup.messages.push({
      ...message,
      isFirstInGroup,
      isLastInGroup,
      showAvatar,
      showName,
      showTime,
    });
  });

  return groups;
}
